import { useId, useState } from 'react';
import type { StatsClub } from '../../lib/training.ts';
import { ancrage, colonne, rayon } from './cadre.ts';

/* Tout le sac sur un seul cadre.
 *
 * ── Ce que ça donne, et ce que ça ne peut pas donner ────────────────────────
 *
 * Sept clubs, sept lignes. Le dessin d'ensemble se lit — le sac monte, ou il
 * descend — mais deux lignes prises au hasard ne se distinguent pas : à sept
 * séries, aucune palette ne sépare toutes les paires, et surtout pas au soleil
 * ni pour qui confond le rouge et le vert. La couleur ne porte donc AUCUNE
 * identité ici : toutes les lignes sont de la même encre pâle, et c'est la
 * sélection qui en détache une.
 *
 * ── Pourquoi une seule ligne en avant à la fois ─────────────────────────────
 *
 * C'est la seule façon honnête de lire sept séries sur un cadre : le fond donne
 * le contexte — où se situe ce club par rapport au reste du sac — et la ligne
 * détachée donne la réponse. Une légende de sept pastilles aurait demandé de
 * faire l'aller-retour entre le bas et le haut de l'écran pour chaque ligne.
 *
 * ── Ce qu'il faut savoir avant d'y lire quoi que ce soit ────────────────────
 *
 * Un club ne fait que quelques balles par sortie. Un 0 % et un 100 % voisins
 * sont monnaie courante et ne veulent rien dire : trois balles décident. La
 * note sous le graphique le dit, et le nombre de balles s'affiche au survol —
 * c'est lui qui donne son poids à chaque point.
 */

const W = 560;
const H = 200;
const PAD_T = 14;
const PAD_B = 22;
const PAD_R = 46;
const PLOT_H = H - PAD_T - PAD_B;

const shortDate = (day: string): string => {
    const [y, m, d] = day.split('-').map(Number);
    return new Date(y!, m! - 1, d!).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
};

const taux = (h: { shots: number; center: number }): number =>
    (h.shots > 0 ? (100 * h.center) / h.shots : 0);

export function ClubsDansLeTemps({ clubs }: { clubs: StatsClub[] }) {
    const titleId = useId();
    const [choisi, setChoisi] = useState<string | null>(null);
    const [hover, setHover] = useState<number | null>(null);
    const [table, setTable] = useState(false);

    /* Deux journées au moins : un point isolé ne fait pas une ligne, et son
     * unique marque se lirait comme un niveau alors que c'est une sortie. */
    const suivis = clubs.filter((c) => c.history.length >= 2);
    if (suivis.length === 0) return null;

    // L'axe des jours est COMMUN : un club qui n'est pas sorti ce jour-là n'y
    // a pas de point, et sa ligne enjambe le trou plutôt que de se décaler.
    const jours = [...new Set(suivis.flatMap((c) => c.history.map((h) => h.jour)))].sort();
    const plotW = W - PAD_R;
    const x = (i: number) => (jours.length === 1 ? 0 : (i / (jours.length - 1)) * plotW);
    const y = (v: number) => PAD_T + PLOT_H - (Math.max(0, Math.min(100, v)) / 100) * PLOT_H;

    const chemin = (c: StatsClub) => c.history
        .map((h, k) => `${k === 0 ? 'M' : 'L'} ${x(jours.indexOf(h.jour))} ${y(taux(h))}`)
        .join(' ');

    /* Les bornes de taille portent sur TOUT le sac, et non sur le seul club
     * regardé : sinon le wedge sorti trois fois par sortie et le driver sorti
     * une fois auraient les mêmes grosses marques, chacun étant le maximum de
     * lui-même. On veut justement pouvoir dire « celui-là, je le travaille
     * peu » en changeant de club. */
    const tous = suivis.flatMap((c) => c.history.map((h) => h.shots));
    const mini = Math.min(...tous);
    const maxi = Math.max(...tous);

    const actif = suivis.find((c) => c.clubId === choisi) ?? null;
    const vu = hover != null && actif
        ? actif.history.find((h) => h.jour === jours[hover]) ?? null
        : null;

    return (
        <div>
            {/* Le nom seul, sans le taux : la carte juste au-dessus le donne
                déjà pour chaque club, et le répéter ici coûtait deux rangées de
                pastilles — plus de hauteur que le graphique lui-même. */}
            <div className="mb-3 flex flex-wrap gap-1.5">
                {suivis.map((c) => {
                    const on = c.clubId === choisi;
                    return (
                        <button key={c.clubId} type="button"
                            onClick={() => setChoisi(on ? null : c.clubId)}
                            aria-pressed={on}
                            className={`min-h-9 rounded-field px-3 py-1.5 text-xs ${
                                on ? 'font-bold' : 'font-semibold'}`}
                            style={on
                                ? { background: 'var(--chart-current)', color: 'var(--color-base-100)' }
                                : { background: 'var(--tuile)', color: 'var(--tuile-encre)' }}>
                            {c.label}
                        </button>
                    );
                })}
            </div>

            <svg viewBox={`0 0 ${W} ${H}`} className="h-auto w-full" role="img" aria-labelledby={titleId}>
                <title id={titleId}>
                    Part de balles centrées par club, du {shortDate(jours[0]!)}
                    {' au '}{shortDate(jours[jours.length - 1]!)}
                </title>

                {[0, 25, 50, 75, 100].map((v) => (
                    <g key={v}>
                        <line x1={0} x2={plotW} y1={y(v)} y2={y(v)}
                            stroke="var(--chart-grid)" strokeWidth={1} />
                        <text x={plotW + 6} y={y(v)} dominantBaseline="central"
                            className="fill-base-content text-[11px] opacity-40">{v}</text>
                    </g>
                ))}

                {/* Le sac au complet, en encre pâle : le contexte, jamais
                    l'identité. */}
                {suivis.map((c) => (
                    <path key={c.clubId} d={chemin(c)} fill="none"
                        stroke="var(--chart-previous)"
                        strokeOpacity={actif ? 0.35 : 0.65}
                        strokeWidth={1.5} strokeLinejoin="round" strokeLinecap="round" />
                ))}

                {/* Le club choisi, par-dessus. */}
                {actif && (
                    <>
                        <path d={chemin(actif)} fill="none" stroke="var(--chart-current)"
                            strokeWidth={2.5} strokeLinejoin="round" strokeLinecap="round" />
                        {/* Le survol se dit en COULEUR et non en taille : depuis
                            que le rayon porte le nombre de balles, grossir la
                            marque survolée revenait à lui prêter des balles
                            qu'elle n'a pas. Un vert plus sombre — celui du club
                            en cours ailleurs dans l'application — garde la
                            teinte de la série et dit seulement « c'est
                            celle-ci ». */}
                        {actif.history.map((h) => (
                            <circle key={h.jour} cx={x(jours.indexOf(h.jour))} cy={y(taux(h))}
                                r={rayon(h.shots, mini, maxi)}
                                fill={hover === jours.indexOf(h.jour)
                                    ? 'var(--club)' : 'var(--chart-current)'}
                                stroke="var(--color-base-100)" strokeWidth={2} />
                        ))}
                    </>
                )}

                {/* LE REPÈRE DE SURVOL : un filet vertical sur la colonne
                    pointée.
                    Ni la taille ni le remplissage ne peuvent plus servir à dire
                    « c'est celle-ci » — la première porte le nombre de balles,
                    le second le verdict. Le filet, lui, ne code rien d'autre
                    que la position du doigt, et il se voit même quand la marque
                    est minuscule. C'est aussi ce que fait n'importe quelle
                    courbe qu'on interroge : on suit une verticale. */}
                {hover != null && (
                    <line x1={x(hover)} x2={x(hover)} y1={PAD_T} y2={PAD_T + PLOT_H}
                        stroke="var(--club)" strokeWidth={1} strokeOpacity={0.45} />
                )}

                {/* Les colonnes de survol : posées en dernier pour rester
                    au-dessus des tracés, et larges d'une journée entière. */}
                {jours.map((j, i) => (
                    <rect key={j} {...colonne(i, jours.length, x, W)} y={0}
                        height={H} fill="transparent"
                        onMouseEnter={() => setHover(i)} onMouseLeave={() => setHover(null)} />
                ))}

                {/* Les deux dates s'effacent pendant le survol : l'étiquette
                    qui les remplace dit déjà le jour. */}
                {hover == null && (
                    <>
                        <text x={0} y={H - 6} className="fill-base-content text-[11px] opacity-50">
                            {shortDate(jours[0]!)}
                        </text>
                        <text x={plotW} y={H - 6} textAnchor="end"
                            className="fill-base-content text-[11px] opacity-50">
                            {shortDate(jours[jours.length - 1]!)}
                        </text>
                    </>
                )}

                {hover != null && (
                    <text x={x(hover)} y={H - 6} textAnchor={ancrage(x(hover), plotW)}
                        className="fill-base-content text-[11px] font-semibold">
                        {shortDate(jours[hover]!)}
                        {vu && ` · ${taux(vu).toFixed(0)} % sur ${vu.shots} balle${vu.shots > 1 ? 's' : ''}`}
                    </text>
                )}
            </svg>

            <p className="mt-1 px-0.5 text-xs opacity-50">
                {actif
                    ? `${actif.label} en avant, le reste du sac en fond · la taille d’un point dit combien de balles. Touchez-le de nouveau pour tout remettre à plat.`
                    : 'Touchez un club pour le détacher du lot.'}
            </p>

            <button type="button" className="btn btn-ghost btn-xs mt-1"
                onClick={() => setTable((t) => !t)} aria-expanded={table}>
                {table ? 'Masquer' : 'Voir'} le tableau
            </button>

            {table && (
                <div className="mt-2 overflow-x-auto">
                    <table className="table table-sm">
                        <thead>
                            <tr>
                                <th>Club</th>
                                <th>Jour</th>
                                <th className="text-right">Balles</th>
                                <th className="text-right">Réussite</th>
                            </tr>
                        </thead>
                        <tbody>
                            {suivis.flatMap((c) => [...c.history].reverse().map((h) => (
                                <tr key={`${c.clubId}-${h.jour}`}>
                                    <td className="whitespace-nowrap">{c.label}</td>
                                    <td className="whitespace-nowrap">{shortDate(h.jour)}</td>
                                    <td className="text-right tabular-nums opacity-70">{h.shots}</td>
                                    <td className="text-right tabular-nums">{taux(h).toFixed(0)}%</td>
                                </tr>
                            )))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}
